export type BroadcasterStatus = 'activo' | 'inactivo' | 'suspendido';
export type VerificationLevel = 'verificado' | 'destacado' | 'ninguno';

export interface BroadcasterProgram {
  id: number;
  name: string;
  radioName: string;
  radioFrequency: string;
  schedule: string;
  role: string;
}

export interface BroadcasterSocial {
  platform: string;
  handle: string;
  followers: number;
  url: string;
}

export interface BroadcasterMetric {
  followers: number;
  listeners: number;
  programs: number;
  interactions: number;
}

export interface Broadcaster {
  id: number;
  name: string;
  photoUrl: string;
  bio: string;
  city: string;
  country: string;
  status: BroadcasterStatus;
  verification: VerificationLevel;
  joinedDate: string;
  metrics: BroadcasterMetric;
  socials: BroadcasterSocial[];
  programs: BroadcasterProgram[];
  specialties: string[];
  languages: string[];
}

export const broadcasters: Broadcaster[] = [
  {
    id: 1,
    name: 'Martín Castellanos',
    photoUrl: 'https://readdy.ai/api/search-image?query=Professional%20male%20radio%20host%20in%20his%20thirties%2C%20warm%20confident%20expression%2C%20studio%20microphone%20in%20background%20softly%20blurred%2C%20wearing%20casual%20elegant%20dark%20shirt%2C%20warm%20studio%20lighting%2C%20editorial%20portrait%20photography%2C%20clean%20professional%20background&width=400&height=400&seq=broadcaster-martin-c&orientation=squarish',
    bio: 'Periodista y locutor con más de 15 años de experiencia en radio. Especialista en programas matutinos y cobertura de eventos en vivo. Ha entrevistado a más de 500 personalidades del mundo político, cultural y deportivo. Su estilo cercano y auténtico lo ha convertido en uno de los locutores más queridos de la radio argentina.',
    city: 'Buenos Aires',
    country: 'Argentina',
    status: 'activo',
    verification: 'verificado',
    joinedDate: '2024-03-10',
    metrics: { followers: 284000, listeners: 620000, programs: 3, interactions: 14500 },
    socials: [
      { platform: 'Instagram', handle: '@martincastellanos', followers: 142000, url: 'https://instagram.com/martincastellanos' },
      { platform: 'Twitter', handle: '@MCastellanosOK', followers: 89000, url: 'https://twitter.com/MCastellanosOK' },
      { platform: 'TikTok', handle: '@martin.castellanos', followers: 53000, url: 'https://tiktok.com/@martin.castellanos' },
      { platform: 'YouTube', handle: '@MartinCastellanosRadio', followers: 41000, url: 'https://youtube.com/@MartinCastellanosRadio' },
    ],
    programs: [
      { id: 101, name: 'Mañanas Argentinas', radioName: 'Radio Éxito FM', radioFrequency: '98.5 FM', schedule: 'Lun-Vie 06:00-10:00', role: 'Conductor Principal' },
      { id: 102, name: 'El Debate', radioName: 'Radio Éxito FM', radioFrequency: '98.5 FM', schedule: 'Mié 20:00-22:00', role: 'Co-Conductor' },
      { id: 103, name: 'Entrevistas Exclusivas', radioName: 'Radio Cultura Digital', radioFrequency: '91.3 FM', schedule: 'Sáb 11:00-13:00', role: 'Conductor' },
    ],
    specialties: ['Periodismo', 'Entrevistas', 'Política', 'Cultura'],
    languages: ['Español', 'Inglés'],
  },
  {
    id: 2,
    name: 'Valentina Ríos',
    photoUrl: 'https://readdy.ai/api/search-image?query=Professional%20female%20radio%20host%20in%20her%20late%20twenties%2C%20friendly%20smile%2C%20modern%20broadcast%20studio%20with%20warm%20lighting%2C%20wearing%20stylish%20cream%20blazer%2C%20headphones%20around%20neck%2C%20editorial%20portrait%20photography%2C%20clean%20warm%20background&width=400&height=400&seq=broadcaster-valentina-r&orientation=squarish',
    bio: 'Comunicadora social y creadora de contenido. Revolucionó la radio juvenil con su programa vespertino mezclando música, cultura pop y entrevistas frescas. Fundadora del movimiento #RadioJoven que impulsa a nuevos talentos. Nominada a Mejor Locutora Revelación 2025. Su comunidad digital supera el medio millón de seguidores.',
    city: 'Ciudad de México',
    country: 'México',
    status: 'activo',
    verification: 'verificado',
    joinedDate: '2024-06-22',
    metrics: { followers: 512000, listeners: 480000, programs: 2, interactions: 28500 },
    socials: [
      { platform: 'Instagram', handle: '@valentina.rios', followers: 280000, url: 'https://instagram.com/valentina.rios' },
      { platform: 'TikTok', handle: '@valeriosradio', followers: 195000, url: 'https://tiktok.com/@valeriosradio' },
      { platform: 'Twitter', handle: '@ValeRiosFM', followers: 37000, url: 'https://twitter.com/ValeRiosFM' },
    ],
    programs: [
      { id: 201, name: 'Tardes con Vale', radioName: 'Estéreo Capital', radioFrequency: '102.1 FM', schedule: 'Lun-Vie 16:00-19:00', role: 'Conductora' },
      { id: 202, name: 'Ranking Top 40', radioName: 'Estéreo Capital', radioFrequency: '102.1 FM', schedule: 'Sáb 14:00-17:00', role: 'Conductora' },
    ],
    specialties: ['Música', 'Cultura Pop', 'Juvenil', 'Redes Sociales'],
    languages: ['Español', 'Inglés'],
  },
  {
    id: 3,
    name: 'Roberto "Beto" Fuentes',
    photoUrl: 'https://readdy.ai/api/search-image?query=Male%20radio%20sports%20commentator%20in%20his%20forties%2C%20energetic%20expression%2C%20wearing%20polo%20shirt%2C%20sports%20broadcast%20studio%20with%20screens%20showing%20match%20highlights%2C%20dynamic%20energy%2C%20editorial%20portrait%2C%20warm%20dramatic%20lighting&width=400&height=400&seq=broadcaster-beto-f&orientation=squarish',
    bio: 'La voz más reconocida del periodismo deportivo radial. Con 22 años de trayectoria, ha cubierto 4 Copas del Mundo y 6 Juegos Olímpicos. Su estilo apasionado y su análisis táctico profundo lo diferencian. Conduce el programa deportivo más escuchado en la franja vespertina. Autor del libro "El Fútbol que Vivimos".',
    city: 'Monterrey',
    country: 'México',
    status: 'activo',
    verification: 'verificado',
    joinedDate: '2024-01-15',
    metrics: { followers: 385000, listeners: 750000, programs: 2, interactions: 32000 },
    socials: [
      { platform: 'Twitter', handle: '@BetoFuentesMX', followers: 210000, url: 'https://twitter.com/BetoFuentesMX' },
      { platform: 'Instagram', handle: '@betofuentesoficial', followers: 128000, url: 'https://instagram.com/betofuentesoficial' },
      { platform: 'YouTube', handle: '@BetoFuentesDeportes', followers: 47000, url: 'https://youtube.com/@BetoFuentesDeportes' },
    ],
    programs: [
      { id: 301, name: 'Deporte Total', radioName: 'Radio Deportiva Total', radioFrequency: '95.7 FM', schedule: 'Lun-Vie 18:00-21:00', role: 'Conductor Principal' },
      { id: 302, name: 'Goles y Jugadas', radioName: 'Radio Deportiva Total', radioFrequency: '95.7 FM', schedule: 'Dom 15:00-18:00', role: 'Conductor' },
    ],
    specialties: ['Deportes', 'Fútbol', 'Análisis Táctico', 'Cobertura Internacional'],
    languages: ['Español', 'Inglés', 'Portugués'],
  },
  {
    id: 4,
    name: 'Carolina Vega',
    photoUrl: 'https://readdy.ai/api/search-image?query=Professional%20female%20radio%20host%20in%20her%20thirties%2C%20elegant%20and%20approachable%2C%20warm%20studio%20with%20acoustic%20panels%2C%20wearing%20elegant%20burgundy%20top%2C%20soft%20professional%20lighting%2C%20editorial%20portrait%20photography&width=400&height=400&seq=broadcaster-carolina-v&orientation=squarish',
    bio: 'Psicóloga de formación, encontró en la radio su verdadera vocación. Creadora del espacio "Conversaciones Reales", un programa íntimo donde aborda salud mental, relaciones y desarrollo personal. Su enfoque empático ha generado una comunidad de oyentes fieles que la consideran una guía. Reconocida por la Asociación de Psicología como divulgadora del año.',
    city: 'Bogotá',
    country: 'Colombia',
    status: 'activo',
    verification: 'verificado',
    joinedDate: '2024-08-05',
    metrics: { followers: 198000, listeners: 340000, programs: 2, interactions: 18200 },
    socials: [
      { platform: 'Instagram', handle: '@carolina.vega.radio', followers: 112000, url: 'https://instagram.com/carolina.vega.radio' },
      { platform: 'Spotify', handle: 'Conversaciones Reales Podcast', followers: 65000, url: 'https://spotify.com/conversacionesreales' },
      { platform: 'Twitter', handle: '@CaroVegaFM', followers: 21000, url: 'https://twitter.com/CaroVegaFM' },
    ],
    programs: [
      { id: 401, name: 'Conversaciones Reales', radioName: 'Radio Élite Digital', radioFrequency: '99.1 FM', schedule: 'Lun-Vie 21:00-23:00', role: 'Conductora' },
      { id: 402, name: 'Bienestar en Sintonía', radioName: 'Radio Costa Azul', radioFrequency: '88.7 FM', schedule: 'Sáb 09:00-11:00', role: 'Conductora Invitada' },
    ],
    specialties: ['Salud Mental', 'Desarrollo Personal', 'Entrevistas Íntimas', 'Divulgación'],
    languages: ['Español'],
  },
  {
    id: 5,
    name: 'Alejandro "Álex" Mendoza',
    photoUrl: 'https://readdy.ai/api/search-image?query=Male%20radio%20DJ%20and%20music%20host%20in%20his%20thirties%2C%20cool%20and%20stylish%20with%20headphones%2C%20modern%20music%20studio%20with%20vinyl%20collection%20background%2C%20wearing%20black%20t-shirt%2C%20warm%20moody%20lighting%2C%20editorial%20portrait&width=400&height=400&seq=broadcaster-alex-m&orientation=squarish',
    bio: 'DJ, productor musical y locutor. Empezó en radios comunitarias y hoy es referencia de la música electrónica y alternativa en Latinoamérica. Su programa nocturno es cita obligada para descubridores de nuevos sonidos. Fundador del sello discográfico independiente "Onda Latina Records". Ha pinchado en festivales como Lollapalooza y Estéreo Picnic.',
    city: 'Medellín',
    country: 'Colombia',
    status: 'activo',
    verification: 'destacado',
    joinedDate: '2024-04-18',
    metrics: { followers: 246000, listeners: 410000, programs: 2, interactions: 21000 },
    socials: [
      { platform: 'Instagram', handle: '@alexmendoza.music', followers: 135000, url: 'https://instagram.com/alexmendoza.music' },
      { platform: 'SoundCloud', handle: '@alex-mendoza-dj', followers: 48000, url: 'https://soundcloud.com/alex-mendoza-dj' },
      { platform: 'TikTok', handle: '@alexmendoza.dj', followers: 63000, url: 'https://tiktok.com/@alexmendoza.dj' },
    ],
    programs: [
      { id: 501, name: 'Onda Nocturna', radioName: 'Estéreo Capital', radioFrequency: '102.1 FM', schedule: 'Lun-Jue 22:00-01:00', role: 'DJ / Conductor' },
      { id: 502, name: 'Sesiones en Vivo', radioName: 'Radio Élite Digital', radioFrequency: '99.1 FM', schedule: 'Vie 21:00-23:00', role: 'DJ Invitado' },
    ],
    specialties: ['Música Electrónica', 'DJ', 'Producción Musical', 'Cultura Alternativa'],
    languages: ['Español', 'Inglés'],
  },
  {
    id: 6,
    name: 'Gabriela Sotomayor',
    photoUrl: 'https://readdy.ai/api/search-image?query=Professional%20female%20news%20anchor%20and%20radio%20host%20in%20her%20forties%2C%20authoritative%20yet%20warm%20presence%2C%20newsroom%20background%20with%20screens%2C%20wearing%20navy%20professional%20attire%2C%20confident%20expression%2C%20editorial%20portrait&width=400&height=400&seq=broadcaster-gabriela-s&orientation=squarish',
    bio: 'Periodista de investigación con 18 años en medios. Conduce el noticiero matutino más respetado de la radio chilena. Ha destapado casos de corrupción que cambiaron políticas públicas. Premio Nacional de Periodismo 2024. Profesora universitaria de Ética Periodística. Su rigor y compromiso con la verdad la han convertido en un referente indiscutible.',
    city: 'Santiago',
    country: 'Chile',
    status: 'activo',
    verification: 'verificado',
    joinedDate: '2024-02-28',
    metrics: { followers: 178000, listeners: 520000, programs: 2, interactions: 9800 },
    socials: [
      { platform: 'Twitter', handle: '@GSotomayorRadio', followers: 98000, url: 'https://twitter.com/GSotomayorRadio' },
      { platform: 'Instagram', handle: '@gabriela.sotomayor', followers: 54000, url: 'https://instagram.com/gabriela.sotomayor' },
      { platform: 'LinkedIn', handle: '/in/gabrielasotomayor', followers: 26000, url: 'https://linkedin.com/in/gabrielasotomayor' },
    ],
    programs: [
      { id: 601, name: 'Noticiero Primera Edición', radioName: 'Radio Industria', radioFrequency: '93.5 FM', schedule: 'Lun-Vie 07:00-09:00', role: 'Conductora' },
      { id: 602, name: 'Investigación Abierta', radioName: 'Radio Cultura Digital', radioFrequency: '91.3 FM', schedule: 'Jue 20:00-21:30', role: 'Conductora / Investigadora' },
    ],
    specialties: ['Periodismo de Investigación', 'Política', 'Noticias', 'Ética'],
    languages: ['Español', 'Inglés'],
  },
  {
    id: 7,
    name: 'Diego Armando Quintero',
    photoUrl: 'https://readdy.ai/api/search-image?query=Young%20male%20radio%20host%20in%20his%20twenties%2C%20enthusiastic%20and%20charismatic%2C%20modern%20podcast%20studio%20setup%2C%20wearing%20colorful%20hoodie%2C%20genuine%20smile%2C%20creative%20lighting%2C%20editorial%20portrait%20photography&width=400&height=400&seq=broadcaster-diego-q&orientation=squarish',
    bio: 'La nueva voz del entretenimiento digital. Con apenas 24 años, ha construido una comunidad masiva hablando de gaming, tecnología y cultura geek. Su programa combina humor, análisis y la participación activa de su audiencia. Creador del podcast "Modo Creativo" con más de 2 millones de descargas. Embajador de marca para varias empresas tech.',
    city: 'Buenos Aires',
    country: 'Argentina',
    status: 'activo',
    verification: 'destacado',
    joinedDate: '2025-01-20',
    metrics: { followers: 420000, listeners: 380000, programs: 1, interactions: 42000 },
    socials: [
      { platform: 'TikTok', handle: '@diegoquintero.ok', followers: 250000, url: 'https://tiktok.com/@diegoquintero.ok' },
      { platform: 'Instagram', handle: '@diego.quintero', followers: 112000, url: 'https://instagram.com/diego.quintero' },
      { platform: 'YouTube', handle: '@ModoCreativoPodcast', followers: 58000, url: 'https://youtube.com/@ModoCreativoPodcast' },
    ],
    programs: [
      { id: 701, name: 'Modo Creativo', radioName: 'Radio Élite Digital', radioFrequency: '99.1 FM', schedule: 'Lun-Vie 19:00-21:00', role: 'Conductor' },
    ],
    specialties: ['Gaming', 'Tecnología', 'Cultura Geek', 'Entretenimiento Digital'],
    languages: ['Español', 'Inglés'],
  },
  {
    id: 8,
    name: 'Lucía Fernández',
    photoUrl: 'https://readdy.ai/api/search-image?query=Professional%20female%20radio%20host%20in%20her%20thirties%2C%20sophisticated%20and%20warm%2C%20classical%20music%20studio%20background%2C%20wearing%20elegant%20silk%20blouse%2C%20soft%20artistic%20lighting%2C%20editorial%20portrait%20photography&width=400&height=400&seq=broadcaster-lucia-f&orientation=squarish',
    bio: 'Musicóloga y locutora especializada en música clásica y world music. Formada en el Conservatorio Nacional, su programa es el espacio cultural más premiado de la radio peruana. Sus análisis musicales son citados por publicaciones internacionales. Ha sido jurado en concursos de piano y directora artística del Festival Internacional de Música de Lima.',
    city: 'Lima',
    country: 'Perú',
    status: 'activo',
    verification: 'verificado',
    joinedDate: '2024-09-12',
    metrics: { followers: 95000, listeners: 230000, programs: 2, interactions: 6200 },
    socials: [
      { platform: 'Instagram', handle: '@lucia.fernandez.musica', followers: 48000, url: 'https://instagram.com/lucia.fernandez.musica' },
      { platform: 'Twitter', handle: '@LuciaFernandezFM', followers: 32000, url: 'https://twitter.com/LuciaFernandezFM' },
      { platform: 'Spotify', handle: 'Clásicos con Lucía Fernández', followers: 15000, url: 'https://spotify.com/clasicosconlucia' },
    ],
    programs: [
      { id: 801, name: 'Clásicos del Mundo', radioName: 'Radio Cultura Digital', radioFrequency: '91.3 FM', schedule: 'Lun-Vie 10:00-12:00', role: 'Conductora' },
      { id: 802, name: 'Notas de Autor', radioName: 'Radio Cultura Digital', radioFrequency: '91.3 FM', schedule: 'Dom 18:00-20:00', role: 'Conductora' },
    ],
    specialties: ['Música Clásica', 'Musicología', 'Crítica Cultural', 'Divulgación Artística'],
    languages: ['Español', 'Francés', 'Italiano'],
  },
  {
    id: 9,
    name: 'Hugo Cárdenas',
    photoUrl: 'https://readdy.ai/api/search-image?query=Male%20radio%20host%20in%20his%20fifties%2C%20wise%20and%20experienced%20presence%2C%20vintage%20radio%20studio%20with%20classic%20equipment%2C%20wearing%20vest%20and%20tie%2C%20warm%20nostalgic%20lighting%2C%20editorial%20portrait&width=400&height=400&seq=broadcaster-hugo-c&orientation=squarish',
    bio: 'Leyenda viva de la radio latinoamericana. Más de 35 años al aire, comenzó en la época dorada de la radio AM. Su voz ha acompañado a tres generaciones de oyentes. Conduce un programa de nostalgia musical que es líder de audiencia desde hace 20 años. Miembro del Salón de la Fama de la Radio. Mentor de nuevas generaciones de locutores.',
    city: 'Caracas',
    country: 'Venezuela',
    status: 'activo',
    verification: 'verificado',
    joinedDate: '2023-11-05',
    metrics: { followers: 156000, listeners: 490000, programs: 1, interactions: 8500 },
    socials: [
      { platform: 'Instagram', handle: '@hugocardenas.radio', followers: 72000, url: 'https://instagram.com/hugocardenas.radio' },
      { platform: 'Facebook', handle: 'Hugo Cárdenas Locutor', followers: 84000, url: 'https://facebook.com/hugocardenaslocutor' },
    ],
    programs: [
      { id: 901, name: 'Noches de Nostalgia', radioName: 'Radio Peninsular', radioFrequency: '101.9 FM', schedule: 'Lun-Sáb 20:00-23:00', role: 'Conductor' },
    ],
    specialties: ['Música Retro', 'Historia Radial', 'Nostalgia', 'Mentoría'],
    languages: ['Español'],
  },
  {
    id: 10,
    name: 'Mariana Duarte',
    photoUrl: 'https://readdy.ai/api/search-image?query=Professional%20female%20radio%20host%20in%20her%20twenties%2C%20fresh%20and%20energetic%2C%20modern%20minimalist%20studio%2C%20wearing%20white%20shirt%2C%20natural%20daylight%20vibe%2C%20editorial%20portrait%20photography&width=400&height=400&seq=broadcaster-mariana-d&orientation=squarish',
    bio: 'Economista convertida en comunicadora. Creadora de "Finanzas al Aire", el primer programa radial dedicado a educación financiera para jóvenes en Latinoamérica. Su enfoque simple y directo ha ayudado a miles de personas a tomar mejores decisiones económicas. Columnista en revistas de negocios y conferencista TEDx. Nombrada una de las 30 menores de 30 más influyentes.',
    city: 'San José',
    country: 'Costa Rica',
    status: 'activo',
    verification: 'destacado',
    joinedDate: '2025-03-15',
    metrics: { followers: 188000, listeners: 290000, programs: 1, interactions: 16200 },
    socials: [
      { platform: 'Instagram', handle: '@marianaduarte.finanzas', followers: 108000, url: 'https://instagram.com/marianaduarte.finanzas' },
      { platform: 'TikTok', handle: '@mariana.duarte', followers: 65000, url: 'https://tiktok.com/@mariana.duarte' },
      { platform: 'LinkedIn', handle: '/in/marianaduarte', followers: 15000, url: 'https://linkedin.com/in/marianaduarte' },
    ],
    programs: [
      { id: 1001, name: 'Finanzas al Aire', radioName: 'Radio Costa Azul', radioFrequency: '88.7 FM', schedule: 'Lun-Vie 08:00-09:00', role: 'Conductora' },
    ],
    specialties: ['Educación Financiera', 'Economía', 'Jóvenes', 'Emprendimiento'],
    languages: ['Español', 'Inglés'],
  },
  {
    id: 11,
    name: 'Ricardo "Ricky" Navarro',
    photoUrl: 'https://readdy.ai/api/search-image?query=Male%20radio%20comedy%20host%20in%20his%20thirties%2C%20funny%20and%20expressive%20face%2C%20colorful%20studio%20with%20quirky%20decorations%2C%20wearing%20graphic%20t-shirt%2C%20vibrant%20lighting%2C%20editorial%20portrait&width=400&height=400&seq=broadcaster-ricky-n&orientation=squarish',
    bio: 'Comediante y locutor que revolucionó las mañanas radiales con humor fresco e irreverente. Su programa "El Desparche" es el más escuchado en la franja matutina alternativa. Combina humor, actualidad y la participación del público con su inconfundible estilo. Ganador del Martín Fierro digital. Su frase "¿Qué más, parceros?" se volvió viral en toda Latinoamérica.',
    city: 'Medellín',
    country: 'Colombia',
    status: 'activo',
    verification: 'verificado',
    joinedDate: '2024-07-08',
    metrics: { followers: 350000, listeners: 570000, programs: 1, interactions: 38000 },
    socials: [
      { platform: 'Instagram', handle: '@rickynavarro', followers: 178000, url: 'https://instagram.com/rickynavarro' },
      { platform: 'TikTok', handle: '@rickynavarro.ok', followers: 142000, url: 'https://tiktok.com/@rickynavarro.ok' },
      { platform: 'Twitter', handle: '@RickyNavarroFM', followers: 30000, url: 'https://twitter.com/RickyNavarroFM' },
    ],
    programs: [
      { id: 1101, name: 'El Desparche', radioName: 'Estéreo Capital', radioFrequency: '102.1 FM', schedule: 'Lun-Vie 06:00-09:00', role: 'Conductor' },
    ],
    specialties: ['Comedia', 'Humor', 'Actualidad', 'Improvisación'],
    languages: ['Español'],
  },
  {
    id: 12,
    name: 'Sofía Rangel',
    photoUrl: 'https://readdy.ai/api/search-image?query=Professional%20female%20radio%20host%20in%20her%20thirties%2C%20calm%20and%20meditative%20presence%2C%20wellness%20studio%20with%20plants%20and%20natural%20elements%2C%20wearing%20linen%20neutral%20clothing%2C%20soft%20natural%20light%2C%20editorial%20portrait&width=400&height=400&seq=broadcaster-sofia-r&orientation=squarish',
    bio: 'Coach de bienestar y locutora especializada en mindfulness y vida consciente. Su programa "Respira" es un oasis de calma en el dial, combinando meditaciones guiadas, entrevistas a expertos en bienestar y música ambiental. Certificada en yoga y meditación. Autora del bestseller "Despierta tu calma interior". Su comunidad crece silenciosamente pero con gran profundidad.',
    city: 'Guadalajara',
    country: 'México',
    status: 'suspendido',
    verification: 'ninguno',
    joinedDate: '2025-05-01',
    metrics: { followers: 42000, listeners: 95000, programs: 1, interactions: 3200 },
    socials: [
      { platform: 'Instagram', handle: '@sofia.rangel.bienestar', followers: 28000, url: 'https://instagram.com/sofia.rangel.bienestar' },
      { platform: 'Spotify', handle: 'Respira Podcast', followers: 14000, url: 'https://spotify.com/respirapodcast' },
    ],
    programs: [
      { id: 1201, name: 'Respira', radioName: 'Radio Costa Azul', radioFrequency: '88.7 FM', schedule: 'Lun-Vie 06:00-07:00', role: 'Conductora' },
    ],
    specialties: ['Mindfulness', 'Bienestar', 'Meditación', 'Yoga'],
    languages: ['Español'],
  },
];

export const broadcasterMetricsSummary = {
  totalBroadcasters: 12,
  totalFollowers: 3074000,
  totalListeners: 5085000,
  totalPrograms: 21,
  totalInteractions: 237900,
  verifiedCount: 8,
  featuredCount: 3,
  unverifiedCount: 1,
  activeCount: 11,
  suspendedCount: 1,
  avgFollowersPerBroadcaster: 256167,
  avgListenersPerBroadcaster: 423750,
  topCountry: 'Argentina',
  topSpecialty: 'Música',
  totalSocialFollowers: 3074000,
};